import { useFirestoreQuery, useFirestoreGeneralOrderBy } from '../../firebase/useFirestore';
import notDeleted from '../../helpers/notDeleted';
import sortSectionFields from '../../helpers/questionnaire/sortSectionFields';
import { QuestionCard } from './ResearchResultCards.jsx';

/**
 * De vragen van één ingevulde vragenlijst, met per vraag een resultaatkaart.
 *
 * Drie schermen tonen dezelfde inzending, en verschillen alleen in HOE ze de
 * antwoorden bij die ene inzending zoeken: het formulier (`FormID`, of een
 * tijdstempel bij oude links), de sessie (`SessionID`) en het persona-formulier
 * (`Persona` + `Timestamp`). Dat verschil zit in `match`; al het andere — de
 * veldenlijst, de secties, de kaartkeuze — is hier één keer beschreven.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @typedef {Array<[string, any]>} SubmissionMatch
 *   Extra gelijkheids-voorwaarden die de antwoorden op één inzending vastzetten,
 *   bijvoorbeeld `[['FormID', 'abc']]` of `[['Persona', 'p'], ['Timestamp', ts]]`.
 *   Een waarde die nog niet bekend is geef je als `''` mee: de query wordt dan
 *   overgeslagen in plaats van met een lege uitkomst terug te komen.
 *   AI-CONTEXT: See docs/pitfalls.md#database-firestore-null-is-a-valid-query-value
 */

/**
 * Eén vraag met de antwoorden die bij deze inzending horen.
 * @param {object} props
 * @param {object} props.field - Het QuestionnaireFields-doc.
 * @param {string} props.researchId - Het onderzoek waar de antwoorden bij horen.
 * @param {SubmissionMatch} props.match - Wat deze inzending onderscheidt.
 */
const SubmissionQuestionCard = ({ field, researchId, match }) => {
    const responses = useFirestoreQuery('QuestionnairesResponses', {
        where: [
            ['ResearchID', '==', researchId || ''],
            ...match.map(([key, value]) => [key, '==', value]),
            ['FieldID', '==', field.ID || ''],
        ],
    });

    return <QuestionCard field={field} responses={responses} />;
};

/**
 * De vragen die bij één sectie horen, in volgorde.
 * @param {object} props
 * @param {string} props.sectionId - ID van de sectie.
 * @param {string} props.researchId - Het onderzoek waar de antwoorden bij horen.
 * @param {SubmissionMatch} props.match - Wat deze inzending onderscheidt.
 */
const SectionFields = ({ sectionId, researchId, match }) => {
    const sectionFields = sortSectionFields((useFirestoreGeneralOrderBy(
        'QuestionnaireFields', 'SectionID', sectionId, 'Position', 'asc'
    ) || []).filter(notDeleted));

    return (
        <>
            {sectionFields.map((field) => (
                <SubmissionQuestionCard
                    key={field.docid || field.ID}
                    field={field}
                    researchId={researchId}
                    match={match}
                />
            ))}
        </>
    );
};

/**
 * @param {object} props
 * @param {string} props.questionnaireId - De vragenlijst van dit onderzoek.
 * @param {string} props.researchId - Het onderzoek waar de antwoorden bij horen.
 * @param {SubmissionMatch} props.match - Wat deze inzending onderscheidt.
 */
const ResearchResultsQuestionList = ({ questionnaireId, researchId, match }) => {
    const fields = (useFirestoreGeneralOrderBy(
        'QuestionnaireFields', 'QuestionnaireID', questionnaireId || '', 'Position', 'asc'
    ) || []).filter(notDeleted);

    const fieldList = Array.isArray(fields) ? fields : [];
    const hasSections = fieldList.some((field) => field.Type === 'section');

    return (
        <div className="research-results-form">

            {!hasSections && (
                <div className="rrf-section-divider"><h2>Vragenlijst</h2></div>
            )}

            {fieldList.map((field) => {
                // Sectievragen renderen binnen hun sectie, niet op het hoogste niveau.
                if (field.Type !== 'section' && field.SectionID !== '') return null;

                if (field.Type === 'section') {
                    return (
                        <div key={field.docid || field.ID}>
                            <div className="rrf-section-divider"><h2>{field.Question}</h2></div>
                            <SectionFields sectionId={field.ID} researchId={researchId} match={match} />
                        </div>
                    );
                }

                return (
                    <SubmissionQuestionCard
                        key={field.docid || field.ID}
                        field={field}
                        researchId={researchId}
                        match={match}
                    />
                );
            })}

        </div>
    );
};

export { SubmissionQuestionCard };
export default ResearchResultsQuestionList;
